/**
 * Tietopohjat: every dataset, grouped by what it is (specs/knowledge-kinds.md, #1369) —
 * source material (a bundle of sources, each tagged with its kind: research, policy,
 * opinion, own reports), opinion polls and statistics — and filterable by that;
 * creating a new one for an upcoming Kuutio; "Tuo tietoaineisto" from a clone package;
 * and, for admins, importing an opinion poll. Source material opens its workspace, a
 * poll its opinion view, the statistics source shows its catalogue.
 * specs/dataset-workspace.md, specs/clone-packages.md, specs/opinion-datasets.md
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Database, Plus, Upload, Users, BarChart3 } from 'lucide-react';
import { Input, Select, Label, Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useAuth } from '../../../components/AuthGate';
import { hasRole } from '../../../utils/roles';
import { listDatasets, createDataset } from './datasetApi';
import ImportPackagePanel from '../../../components/Clone/ImportPackagePanel';
import ImportOpinionForm from '../OpinionDatasets/ImportOpinionForm';
import { Section, SectionTitle, Lead, FieldRow, Card, CardTitle, Meta, StatusChip, KindChip, ErrorText } from './styles';

const SHAPES = ['text', 'statements', 'series'];
// The kinds a text dataset's sources come in, in the order they are listed
const SOURCE_KINDS = ['research', 'policy', 'opinion', 'testimony'];

/** How many indexed sources of each kind a text dataset holds, e.g. "Tutkimus 3 · Linjaukset 10". */
function SourceKindCounts({ kinds, t }) {
  const present = SOURCE_KINDS.filter((k) => kinds?.[k] > 0);
  return present.map((k) => (
    <KindChip key={k} $kind={k}>{t(`knowledgeKinds.kinds.${k}`)} {kinds[k]}</KindChip>
  ));
}

function StatusCounts({ counts, t }) {
  return Object.entries(counts || {}).map(([status, n]) => (
    <StatusChip key={status} $status={status}>
      {t(`datasetWorkspace.statuses.${status}`)} {n}
    </StatusChip>
  ));
}

/** One dataset: what it is, how much it holds, and where it opens — by its shape. */
function DatasetCard({ d, t, onOpen, onOpenOpinion, onOpenSeries }) {
  const kindChip = <KindChip $kind={d.kind}>{t(`knowledgeKinds.kinds.${d.kind}`)}</KindChip>;
  if (d.shape === 'series') {
    // A file series dataset opens its tables (specs/file-series-datasets.md); StatFin shows its catalogue
    const isFile = d.source_format === 'file';
    return (
      <Card as={isFile ? 'button' : 'div'} type={isFile ? 'button' : undefined} onClick={isFile ? () => onOpenSeries?.(d.id) : undefined}>
        <CardTitle>{d.name}</CardTitle>
        <Meta>
          <code>{d.id}</code>
          {kindChip}
          {d.is_global && <span>{t('datasetWorkspace.global')}</span>}
          {isFile
            ? <span>{t('knowledgeKinds.series.tableCount', { tables: d.series_tables || 0 })}</span>
            : <span>{d.series_tables ? t('knowledgeKinds.seriesTables', { tables: d.series_tables }) : t('knowledgeKinds.seriesNoCatalogue')}</span>}
        </Meta>
      </Card>
    );
  }
  if (d.shape === 'statements') {
    const ready = d.group_count > 0 && d.unprofiled_groups === 0;
    return (
      <Card type="button" onClick={() => onOpenOpinion?.(d.id)}>
        <CardTitle>{d.name}</CardTitle>
        <Meta>
          <code>{d.id}</code>
          {kindChip}
          <span>{t('opinionDatasets.counts', { opinions: d.statement_count || 0, groups: d.group_count || 0 })}</span>
          <StatusChip $status={ready ? 'indexed' : 'needs_review'}>
            {t(ready ? 'opinionDatasets.profilesReady' : 'opinionDatasets.profilesMissing')}
          </StatusChip>
          {!d.instance_count && <span>{t('datasetWorkspace.notAttached')}</span>}
        </Meta>
      </Card>
    );
  }
  return (
    <Card type="button" onClick={() => onOpen(d.id)}>
      <CardTitle>{d.name}</CardTitle>
      <Meta>
        <code>{d.id}</code>
        <SourceKindCounts kinds={d.source_kinds} t={t} />
        {d.is_global && <span>{t('datasetWorkspace.global')}</span>}
        {d.topic && <span>{d.topic}</span>}
        <span>{t('datasetWorkspace.indexCount', { documents: d.document_count, chunks: d.chunk_count })}</span>
        {d.source_count > 0 && <span>{t('datasetWorkspace.sourceCount', { count: d.source_count })}</span>}
        {!d.is_global && !d.instance_count && <span>{t('datasetWorkspace.notAttached')}</span>}
      </Meta>
      {d.source_count > 0 && <Meta><StatusCounts counts={d.status_counts} t={t} /></Meta>}
    </Card>
  );
}

export default function DatasetList({ onOpen, onOpenOpinion, onOpenSeries, shape: shownShape = null, onShape }) {
  const { t } = useTranslation();
  // Creating and importing are facilitator work (specs/access-model.md,
  // decision 1); someone here on a bare dataset grant only sees their list.
  const { user } = useAuth();
  const mayCreate = hasRole(user, 'facilitator');
  const [datasets, setDatasets] = useState([]);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('fi');
  const [creating, setCreating] = useState(false);
  const [importingOpinion, setImportingOpinion] = useState(false);
  const [seriesName, setSeriesName] = useState('');
  const [creatingSeries, setCreatingSeries] = useState(false);

  const handleCreateSeries = async (e) => {
    e.preventDefault();
    setCreatingSeries(true);
    try {
      const created = await createDataset({ name: seriesName, language, shape: 'series' });
      setSeriesName('');
      onOpenSeries?.(created.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingSeries(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setDatasets(await listDatasets());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const created = await createDataset({ name, topic, language });
      setName('');
      setTopic('');
      onOpen(created.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const shapeOf = (d) => d.shape || 'text';
  const presentShapes = SHAPES.filter((k) => datasets.some((d) => shapeOf(d) === k));
  const shown = shownShape ? datasets.filter((d) => shapeOf(d) === shownShape) : datasets;

  return (
    <>
      {mayCreate && (
      <Section as="form" onSubmit={handleCreate}>
        <SectionTitle><Plus size={18} /> {t('datasetWorkspace.newDataset')}</SectionTitle>
        <Lead>{t('datasetWorkspace.listLead')}</Lead>
        <FieldRow>
          <div>
            <Label htmlFor="ds-name">{t('datasetWorkspace.nameLabel')}</Label>
            <Input id="ds-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('datasetWorkspace.namePlaceholder')} required />
          </div>
          <div>
            <Label htmlFor="ds-topic">{t('datasetWorkspace.topicLabel')}</Label>
            <Input id="ds-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder={t('datasetWorkspace.topicPlaceholder')} />
          </div>
          <div>
            <Label htmlFor="ds-lang">{t('datasetWorkspace.languageLabel')}</Label>
            <Select id="ds-lang" value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="fi">suomi</option>
              <option value="en">English</option>
              <option value="sv">svenska</option>
            </Select>
          </div>
        </FieldRow>
        <Lead>{t('knowledgeKinds.sourceKindsLead')}</Lead>
        <Actions>
          <Button type="submit" $variant="primary" disabled={creating || !name.trim()}>
            {t('datasetWorkspace.create')}
          </Button>
          {error && <StatusLine $error>{error}</StatusLine>}
        </Actions>
      </Section>
      )}

      {/* A file series dataset: tidy sheets of numbers (specs/file-series-datasets.md, #1562) */}
      {mayCreate && (
      <Section as="form" onSubmit={handleCreateSeries}>
        <SectionTitle><BarChart3 size={18} /> {t('knowledgeKinds.series.newTitle')}</SectionTitle>
        <Lead>{t('knowledgeKinds.series.newLead')}</Lead>
        <FieldRow>
          <div>
            <Label htmlFor="series-name">{t('datasetWorkspace.nameLabel')}</Label>
            <Input id="series-name" value={seriesName} onChange={(e) => setSeriesName(e.target.value)} required />
          </div>
        </FieldRow>
        <Actions>
          <Button type="submit" $variant="primary" disabled={creatingSeries || !seriesName.trim()}>
            {t('knowledgeKinds.series.create')}
          </Button>
        </Actions>
      </Section>
      )}

      {/* Clone packages run on the admin-only clone API. */}
      {hasRole(user, 'admin') && (
      <Section>
        <SectionTitle><Upload size={18} /> {t('clone.importDataset')}</SectionTitle>
        <ImportPackagePanel kind="dataset" onOpenClone={(r) => onOpen(r.id)} />
      </Section>
      )}

      {/* Opinion statements datasets are imported, not built from sources; admin work */}
      {hasRole(user, 'admin') && (
      <Section>
        <SectionTitle><Users size={18} /> {t('knowledgeKinds.importOpinion')}</SectionTitle>
        <Lead>{t('opinionDatasets.lead')}</Lead>
        {importingOpinion ? (
          <ImportOpinionForm onCancel={() => setImportingOpinion(false)} onDone={() => { load(); }} />
        ) : (
          <Actions>
            <Button type="button" onClick={() => setImportingOpinion(true)}>
              <Plus /> {t('opinionDatasets.import.open')}
            </Button>
          </Actions>
        )}
      </Section>
      )}

      <Section>
        <SectionTitle><Database size={18} /> {t('datasetWorkspace.listTitle')}</SectionTitle>
        {presentShapes.length > 1 && (
          <Meta role="group" aria-label={t('knowledgeKinds.filterLabel')}>
            {[null, ...presentShapes].map((k) => (
              <Button key={k || 'all'} type="button" $size="small" $variant={shownShape === k ? 'primary' : undefined}
                aria-pressed={shownShape === k} onClick={() => onShape?.(k)}>
                {k ? t(`knowledgeKinds.shapes.${k}`) : t('knowledgeKinds.all')}
              </Button>
            ))}
          </Meta>
        )}
        {!datasets.length && !error && <Lead>{t('datasetWorkspace.empty')}</Lead>}
        {error && !datasets.length && <ErrorText>{error}</ErrorText>}
        {shown.map((d) => (
          <DatasetCard key={d.id} d={d} t={t} onOpen={onOpen} onOpenOpinion={onOpenOpinion} onOpenSeries={onOpenSeries} />
        ))}
      </Section>
    </>
  );
}
